/** @prettier */

/**
 * 空調機設定用ツリービュー
 */

import {TreeView, TreeItem} from '@mui/x-tree-view';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {useNavigate, useLocation, useParams} from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import {useState, useEffect} from 'react';
import {Box} from '@mui/material';
import {useTranslation} from 'react-i18next';

//interface import
import * as apiIF from '../Type/api';
//Style import
import '../css/common/common.css';

import {iconBlack} from '../define';

/**
 * 空調機設定用ツリービューコンポーネント
 * @param props
 * - defaultExpanded デフォルトで開く階層を指定
 * - gwInfo ゲートウェイの情報
 * @returns JSX
 *
 * 開きたい階層に対して、ツリーのIDに指定した文字列を、カンマ繋ぎで配列に指定
 */
const UnitSettingTreeView = (props: {
  defaultExpanded: string[];
  gwInfo: apiIF.res.getCompositionsGateway | null;
}) => {
  const navigate = useNavigate();
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {siteID} = useParams();

  const [expanded, setExpanded] = useState<string[]>(props.defaultExpanded); //ツリービュー展開する位置

  const trans = useTranslation().t; //翻訳関数

  //ツリービュー展開する位置を更新
  useEffect(() => {
    setExpanded(props.defaultExpanded);
  }, [props.defaultExpanded]);

  /**
   * ツリービューが展開・折りたたまれた時の処理
   * @param event
   * @param nodeIds
   */
  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };

  //prettier-ignore
  type UnitTreeItemType = {
    id          : string;
    nodeID      : string;
    className   ?: string;
    label       : React.ReactNode;
    labelClick  ?: React.MouseEventHandler<HTMLButtonElement>;
    children    ?: UnitTreeItemType[];
  };
  /**
   * SL通信設定用のツリービューの項目
   * @param siteID サイトID
   * @param gwInfo ゲートウェイの情報
   * @returns ツリービューの項目
   */
  const slUnitTreeItemData = (
    siteID: string,
    gwInfo: apiIF.resParameters.gatewayInformations
  ) => {
    //prettier-ignore
    let treeItem: UnitTreeItemType = {
      id          : `treeLink${gwInfo['gateway-id']}`,
      nodeID      : gwInfo['gateway-id'],
      label       : gwInfo['gateway-name'],
      labelClick  : () => {
        navigate(
          `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication${location.search}`
        );
      },
      children    : [
        //SL1
        {
          id          : 'mv-treeLinkSL1',
          nodeID      : `${gwInfo['gateway-id']}sl1`,
          label       : trans('UnitSettingTreeView.label.superlink1'),
          labelClick  : () => {
            navigate(
              `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/sl1/sl-ou-registration${location.search}`
            );
          },
          //OU or Other
          children    : gwInfo.compositions === undefined ? [] : [
            ...gwInfo.compositions['sl-system-1']['sl-unit'].map(ouInfo => ({
              id          : `treeLink${ouInfo['outdoor-id']}`,
              nodeID      : ouInfo['outdoor-id'],
              label       : ouInfo['outdoor-unit-name'],
              labelClick  : () => {
                navigate(
                  `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/sl1/sl-ou-registration/${ouInfo['outdoor-id']}/sl-iu-registration${location.search}`
                );
              },
              //IU
              children    : ouInfo['indoor-unit'].map(iuInfo => ({
                id      : `treeLink${iuInfo['unit-id']}`,
                nodeID  : iuInfo['unit-id'],
                label   : iuInfo['indoor-unit-name'],
              })),
            })),
            //Other
            {
              id          : 'mv-treeLinkSL1Other',
              nodeID      : `${gwInfo['gateway-id']}sl1other`,
              label       : trans('UnitSettingTreeView.label.interface'),
              labelClick  : () => {
                navigate(
                  `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/sl1/sl-ou-registration/sl-other-registration${location.search}`
                );
              },
              children    : gwInfo.compositions['sl-system-1']['slif-unit'].map(
                unitInfo => ({
                  id      : `treeLink${unitInfo['unit-id']}`,
                  nodeID  : unitInfo['unit-id'],
                  label   : unitInfo['unit-name'],
                })
              ),
            },
          ],
        },
        //SL2
        {
          id          : `treeLink${gwInfo['gateway-name']}`,
          nodeID      : `${gwInfo['gateway-id']}sl2`,
          label       : trans('UnitSettingTreeView.label.superlink2'),
          labelClick  : () => {
            navigate(
              `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/sl2/sl-ou-registration${location.search}`
            );
          },
          //OU or Other
          children: gwInfo.compositions === undefined ? [] : [
            ...gwInfo.compositions['sl-system-2']['sl-unit'].map(ouInfo => ({
              id          : `treeLink${ouInfo['outdoor-id']}`,
              nodeID      : ouInfo['outdoor-id'],
              label       : ouInfo['outdoor-unit-name'],
              labelClick  : () => {
                navigate(
                  `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/sl2/sl-ou-registration/${ouInfo['outdoor-id']}/sl-iu-registration${location.search}`
                );
              },
              //IU
              children: ouInfo['indoor-unit'].map(iuInfo => ({
                id      : `treeLink${iuInfo['unit-id']}`,
                nodeID  : iuInfo['unit-id'],
                label   : iuInfo['indoor-unit-name'],
              })),
            })),
            //Other
            {
              id          : 'mv-treeLinkSL2Other',
              nodeID      : `${gwInfo['gateway-id']}sl2other`,
              label       : trans('UnitSettingTreeView.label.interface'),
              labelClick  : () => {
                navigate(
                  `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/sl2/sl-ou-registration/sl-other-registration${location.search}`
                );
              },
              children    : gwInfo.compositions['sl-system-2']['slif-unit'].map(
                unitInfo => ({
                  id      : `treeLink${unitInfo['unit-id']}`,
                  nodeID  : unitInfo['unit-id'],
                  label   : unitInfo['unit-name'],
                })
              ),
            },
          ],
        },
      ],
    };

    //Modbus機能が有効の場合
    if (gwInfo['modbus-tcp'] === 'active') {
      if (treeItem.children === undefined) treeItem.children = [];
      //prettier-ignore
      treeItem.children.push({
        id          : `${gwInfo['gateway-id']}MODBUS`,
        nodeID      : `${gwInfo['gateway-id']}modbus`,
        label       : trans('UnitSettingTreeView.label.modbus'),
        labelClick  : () => {
          navigate(
            `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication${location.search}`
          );
        },
        //OU
        children    : gwInfo.compositions === undefined ? [] : gwInfo.compositions['modbus-system'].map(ouInfo => ({
          id          : `treeLink${ouInfo['outdoor-id']}`,
          nodeID      : ouInfo['outdoor-id'],
          label       : ouInfo['outdoor-unit-name'],
          labelClick  : () => {
            navigate(
              `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/modubs/modbus-ou-registration/${ouInfo['outdoor-id']}/modbus-iu-registration${location.search}`
            );
          },
          //IU
          children    : ouInfo['indoor-unit'].map(iuInfo => ({
            id      : `treeLink${iuInfo['unit-id']}`,
            nodeID  : iuInfo['unit-id'],
            label   : iuInfo['indoor-unit-name'],
          })),
        })),
      });
    }

    return treeItem;
  };
  /**
   * RT通信設定用のツリービューの項目
   * @param siteID サイトID
   * @param gwInfo ゲートウェイの情報
   * @returns ツリービューの項目
   */
  const rtUnitTreeItemData = (
    siteID: string,
    gwInfo: apiIF.resParameters.gatewayInformations
  ) => {
    //prettier-ignore
    let treeItem: UnitTreeItemType = {
      id          : `treeLink${gwInfo['gateway-id']}`,
      nodeID      : gwInfo['gateway-id'],
      label       : gwInfo['gateway-name'],
      labelClick  : () => {
        navigate(
          `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication${location.search}`
        );
      },
      children: [
        //RT1
        {
          id          : 'mv-treeLinkRT1',
          nodeID      : `${gwInfo['gateway-id']}rt1`,
          label       : trans('UnitSettingTreeView.label.remoteTerminal1'),
          labelClick  : () => {
            navigate(
              `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/rt1/rt-registration${location.search}`
            );
          },
          children    : gwInfo.compositions === undefined ? [] : gwInfo['compositions']['rt-system-1'].map(unitInfo => ({
            id         : `treeLink${unitInfo['unit-id']}`,
            nodeID     : unitInfo['unit-id'],
            label      : unitInfo['unit-name'],
            labelClick : unitInfo['indoor-unit'].length === 0 ?
              undefined :
              () => {
                navigate(
                  `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/rt1/rt-registration/${unitInfo['unit-id']}/rt-iu-registration${location.search}`
                );
              },
            //IU
            children   : unitInfo['indoor-unit'].map(iuInfo => ({
              id      : `treeLink${iuInfo['unit-id']}`,
              nodeID  : iuInfo['unit-id'],
              label   : iuInfo['indoor-unit-name'],
            })),
          })),
        },
        //RT2
        {
          id          : 'mv-treeLinkRT2',
          nodeID      : `${gwInfo['gateway-id']}rt2`,
          label       : trans('UnitSettingTreeView.label.remoteTerminal2'),
          labelClick  : () => {
            navigate(
              `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/rt2/rt-registration${location.search}`
            );
          },
          children    : gwInfo.compositions === undefined ? [] : gwInfo['compositions']['rt-system-2'].map(unitInfo => ({
            id         : `treeLink${unitInfo['unit-id']}`,
            nodeID     : unitInfo['unit-id'],
            label      : unitInfo['unit-name'],
            labelClick : unitInfo['indoor-unit'].length === 0 ?
              undefined :
              () => {
                navigate(
                  `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/rt2/rt-registration/${unitInfo['unit-id']}/rt-iu-registration${location.search}`
                );
              },
            //IU
            children   : unitInfo['indoor-unit'].map(iuInfo => ({
              id      : `treeLink${iuInfo['unit-id']}`,
              nodeID  : iuInfo['unit-id'],
              label   : iuInfo['indoor-unit-name'],
            })),
          })),
        },
      ],
    };

    //Modbus機能が有効の場合
    if (gwInfo['modbus-tcp'] === 'active') {
      if (treeItem.children === undefined) treeItem.children = [];
      //prettier-ignore
      treeItem.children.push({
        id          : `${gwInfo['gateway-id']}MODBUS`,
        nodeID      : `${gwInfo['gateway-id']}modbus`,
        label       : trans('UnitSettingTreeView.label.modbus'),
        labelClick  : () => {
          navigate(
            `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication${location.search}`
          );
        },
        //OU
        children    : gwInfo.compositions === undefined ? [] : gwInfo.compositions['modbus-system'].map(ouInfo => ({
          id          : `treeLink${ouInfo['outdoor-id']}`,
          nodeID      : ouInfo['outdoor-id'],
          label       : ouInfo['outdoor-unit-name'],
          labelClick  : () => {
            navigate(
              `/pages/my-site/site-detail/${siteID}/gw-setting/${gwInfo['gateway-id']}/communication/modubs/modbus-ou-registration/${ouInfo['outdoor-id']}/modbus-iu-registration${location.search}`
            );
          },
          //IU
          children    : ouInfo['indoor-unit'].map(iuInfo => ({
            id      : `treeLink${iuInfo['unit-id']}`,
            nodeID  : iuInfo['unit-id'],
            label   : iuInfo['indoor-unit-name'],
          })),
        })),
      });
    }

    return treeItem;
  };
  /**
   * ツリービュー作成
   * @param data ツリービューの項目
   * @returns JSX
   */
  const RenderTree = (data: UnitTreeItemType) => (
    <TreeItem
      key={data.id}
      nodeId={data.nodeID}
      id={data.id}
      label={
        <>
          <Box
            component="span"
            className={`gwSetting__treeView__item ${
              data.className === undefined ? '' : data.className
            }`}
            title={data.label?.toString()}>
            {data.label}
          </Box>
          {/** 末端ではない場合、遷移ボタンを作成 */}
          {data.labelClick === undefined ? (
            <></>
          ) : (
            <IconButton onClick={data.labelClick}>
              <img
                src={iconBlack.CHEVRON_RIGHT.src}
                alt={`${trans(
                  `UnitSettingTreeView.image.alternativeText.${iconBlack.CHEVRON_RIGHT.alt}`
                )}`}
              />
            </IconButton>
          )}
        </>
      }>
      {Array.isArray(data.children)
        ? data.children.map(data => RenderTree(data))
        : null}
    </TreeItem>
  );

  if (siteID === undefined || props.gwInfo == null) return <></>;
  return (
    <>
      <TreeView
        disableSelection={true}
        defaultCollapseIcon={<RemoveIcon />}
        defaultExpandIcon={<AddIcon />}
        expanded={expanded}
        onNodeToggle={handleToggle}
        className="gwSetting__treeView">
        {props.gwInfo['gateway-informations'].map(gwInfo =>
          gwInfo['system'] === 'sl'
            ? RenderTree(slUnitTreeItemData(siteID, gwInfo))
            : RenderTree(rtUnitTreeItemData(siteID, gwInfo))
        )}
      </TreeView>
    </>
  );
};
export default UnitSettingTreeView;
